import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CustomPaginationTable from '../../components/table/CustomPaginationTable';
import { fetchOrderQuery, setQueryParams, exportOrderQuery } from '../../redux/orderQuerySlice';
import './ViewSales.css';
import { COMPLETED } from '../../utils/constant';

function ViewSales() {
  const dispatch = useDispatch();
  const { data, loading, error, pagination, exportLoading } = useSelector(state => state.orderQuery);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const queryParams = {
      page: 0,
      size: 10,
      status: COMPLETED
    };
    dispatch(setQueryParams(queryParams));
    dispatch(fetchOrderQuery(queryParams));
  }, [dispatch]);

  const formatDate = (date) => {
    if (!date) return '';
    return date.toISOString().replace('T', ' ').split('.')[0];
  };

  const buildQueryParams = useCallback((page = 0) => {
    const params = {
      page: page,
      size: 10,
      status: COMPLETED
    };

    if (startDate) {
      params.startdate = formatDate(startDate);
    }

    if (endDate) {
      params.enddate = formatDate(endDate);
    }

    return params;
  }, [startDate, endDate]);

  const handleSearch = useCallback(() => {
    const queryParams = buildQueryParams(0);
    dispatch(setQueryParams(queryParams));
    dispatch(fetchOrderQuery(queryParams));
    setCurrentPage(1);
  }, [dispatch, buildQueryParams]);

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);

    const queryParams = {
      page: 0,
      size: 10,
      status: COMPLETED
    };
    dispatch(setQueryParams(queryParams));
    dispatch(fetchOrderQuery(queryParams));
  };

  const handleExport = () => {
    const exportParams = {
      status: COMPLETED
    };

    if (startDate) {
      exportParams.startdate = formatDate(startDate);
    }

    if (endDate) {
      exportParams.enddate = formatDate(endDate);
    }

    dispatch(exportOrderQuery(exportParams));
  };

  const handlePageChange = (page) => {
    const apiPage = page - 1;
    const queryParams = buildQueryParams(apiPage);
    dispatch(fetchOrderQuery(queryParams));
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    const queryParams = {
      ...buildQueryParams(0),
      size: newPerPage
    };
    dispatch(fetchOrderQuery(queryParams));
    setCurrentPage(1);
  };

  const completedOrders = data ? data.filter(item => item.orderStatus === COMPLETED) : [];

  const tableData = completedOrders.map(item => ({
    orderNo: item.orderNo,
    orderStatus: item.orderStatus,
    purchaseDate: item.createdDatetime || 'N/A',
    email: item.orderDetail?.email || 'N/A',
    iccid: item.orderDetail?.esim?.iccid || 'N/A',
    mvneOrderId: item.orderDetail?.mvneOrderId || 'N/A',
    product: item.orderDetail?.product?.name || 'N/A',
    afterDiscountPrice: item.orderDetail?.product?.price
  }));

  const columns = [
    {
      name: 'Order Number',
      selector: row => row.orderNo,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.orderNo}
        </div>
      ),
      wrap: true
    },
    {
      name: 'Order Status',
      selector: row => row.orderStatus,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.orderStatus}
        </div>
      ),
      wrap: true
    },
    {
      name: 'Purchase Date',
      selector: row => {
        if (row.purchaseDate === 'N/A') return 'N/A';
        const date = new Date(row.purchaseDate);
        return date.toLocaleString();
      },
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.purchaseDate === 'N/A' ? 'N/A' : new Date(row.purchaseDate).toLocaleString()}
        </div>
      ),
      wrap: true
    },
    {
      name: 'Email',
      selector: row => row.email,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.email}
        </div>
      ),
      wrap: true
    },
    {
      name: 'ICCID',
      selector: row => row.iccid,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.iccid}
        </div>
      ),
      wrap: true
    },
    {
      name: 'MVNE Order ID',
      selector: row => row.mvneOrderId,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.mvneOrderId}
        </div>
      ),
      wrap: true
    },
    {
      name: 'Product',
      selector: row => row.product,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.product}
        </div>
      ),
      sortable: true,
      wrap: true
    },
    {
      name: 'After Discount Price',
      selector: row => `${parseFloat(row.afterDiscountPrice).toFixed(2)}`,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {parseFloat(row.afterDiscountPrice).toFixed(2)}
        </div>
      ),
      sortable: true,
      wrap: true
    }
  ];

  return (
    <div className='view-sales-container'>
      <div className="view-sales-content">
        <h3>View Sales</h3>

        <div className="view-sales-search-section">
          <div className="view-sales-search-form">
            <div className="view-sales-form-group">
              <DatePicker
                selected={startDate}
                onChange={date => setStartDate(date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="start date"
                className="view-sales-date-input"
                showIcon
              />
            </div>

            <div className="view-sales-form-group">
              <DatePicker
                selected={endDate}
                onChange={date => setEndDate(date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="end date"
                className="view-sales-date-input"
                showIcon
              />
            </div>

            <div className="view-sales-button-group">
              <button
                className="view-sales-search-btn"
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? 'SEARCHING...' : 'SEARCH'}
              </button>
              <button
                className="view-sales-search-btn"
                onClick={handleReset}
                disabled={loading}
              >
                RESET
              </button>
              <button
                className="view-sales-search-btn"
                onClick={handleExport}
                disabled={exportLoading || loading || completedOrders.length === 0}
              >
                {exportLoading ? 'EXPORTING...' : 'EXPORT'}
              </button>
            </div>
          </div>
        </div>

        <div className="view-sales-table-section">
          <CustomPaginationTable
            data={tableData}
            totalRows={completedOrders.length || 0}
            loading={loading}
            perPage={pagination.pageSize || 10}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            columns={columns}
            onPerRowsChange={handlePerRowsChange}
            isHiddenPag={false}
          />
        </div>
      </div>
    </div>
  );
}

export default ViewSales;